import {isTextEntryTarget} from '../utils/keyboard';
import {useCallback, useEffect, useReducer, useRef} from 'react';
import {Edge, Node} from 'reactflow';

const HISTORY_LIMIT = 50;

export interface EditorSnapshot {
    nodes: Node[];
    edges: Edge[];
    /** Names the change made *from* this state (the agent labels its batches); Undo and Redo show it. */
    label?: string;
}

interface UseEditorHistoryOptions {
    nodes: Node[];
    edges: Edge[];
    shortcutsEnabled?: boolean;
    onRestore: (snapshot: EditorSnapshot) => void;
}

const stripFunctions = (data: Record<string, unknown>) => Object.fromEntries(
    Object.entries(data).filter(([, value]) => typeof value !== 'function')
);

export const createSnapshot = (nodes: Node[], edges: Edge[]): EditorSnapshot => ({
    nodes: nodes.map(node => structuredClone({
        ...node,
        data: stripFunctions((node.data ?? {}) as Record<string, unknown>)
    }) as Node),
    edges: edges.map(edge => structuredClone(edge) as Edge)
});

const withLabel = (snapshot: EditorSnapshot, label?: string): EditorSnapshot => (label ? {...snapshot, label} : snapshot);

const sameGraph = (a: EditorSnapshot, b: EditorSnapshot): boolean =>
    JSON.stringify([a.nodes, a.edges]) === JSON.stringify([b.nodes, b.edges]);

export const useEditorHistory = ({nodes, edges, shortcutsEnabled = true, onRestore}: UseEditorHistoryOptions) => {
    const past = useRef<EditorSnapshot[]>([]);
    const future = useRef<EditorSnapshot[]>([]);
    const latest = useRef({nodes, edges});
    const [, forceRender] = useReducer((count: number) => count + 1, 0);

    useEffect(() => {
        latest.current = {nodes, edges};
    });

    const captureSnapshot = useCallback((snapshot: EditorSnapshot, label?: string) => {
        const previous = past.current[past.current.length - 1];
        if (previous && sameGraph(previous, snapshot)) return;

        past.current = [...past.current, withLabel(snapshot, label)].slice(-HISTORY_LIMIT);
        future.current = [];
        forceRender();
    }, []);

    const capture = useCallback(() => {
        captureSnapshot(createSnapshot(latest.current.nodes, latest.current.edges));
    }, [captureSnapshot]);

    const undo = useCallback(() => {
        const previous = past.current[past.current.length - 1];
        if (!previous) return;

        past.current = past.current.slice(0, -1);
        // The state being left carries the same label, so Redo names the change it reapplies.
        future.current = [
            ...future.current,
            withLabel(createSnapshot(latest.current.nodes, latest.current.edges), previous.label)
        ].slice(-HISTORY_LIMIT);
        forceRender();
        onRestore(previous);
    }, [onRestore]);

    const redo = useCallback(() => {
        const next = future.current[future.current.length - 1];
        if (!next) return;

        future.current = future.current.slice(0, -1);
        past.current = [
            ...past.current,
            withLabel(createSnapshot(latest.current.nodes, latest.current.edges), next.label)
        ].slice(-HISTORY_LIMIT);
        forceRender();
        onRestore(next);
    }, [onRestore]);

    const reset = useCallback(() => {
        if (!past.current.length && !future.current.length) return;
        past.current = [];
        future.current = [];
        forceRender();
    }, []);

    useEffect(() => {
        if (!shortcutsEnabled) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.repeat || event.defaultPrevented) return;
            if (!event.metaKey && !event.ctrlKey) return;
            if (isTextEntryTarget(event.target)) return;

            const key = event.key.toLowerCase();

            if (key === 'z') {
                event.preventDefault();
                if (event.shiftKey) {
                    redo();
                } else {
                    undo();
                }
                return;
            }

            if (key === 'y' && event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                redo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [shortcutsEnabled, undo, redo]);

    return {
        canUndo: past.current.length > 0,
        canRedo: future.current.length > 0,
        undoLabel: past.current[past.current.length - 1]?.label,
        redoLabel: future.current[future.current.length - 1]?.label,
        capture,
        captureSnapshot,
        undo,
        redo,
        reset
    };
};
